import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Receipt, CheckCircle, AlertTriangle, Loader2, CreditCard, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicInvoicePayment() {
  const [searchParams] = useSearchParams();
  const invoiceId = searchParams.get('invoice');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!invoiceId) {
      setError('No invoice specified');
      setLoading(false);
      return;
    }
    axios.get(`${BACKEND_URL}/api/invoices/public/${invoiceId}`)
      .then(res => {
        setInvoice(res.data);
      })
      .catch(() => setError('Invoice not found'))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  const handlePayNow = async () => {
    if (!invoice) return;
    setRedirecting(true);

    try {
      const items = (invoice.items || []).map(item => ({
        name: item.name || item.service || 'Service',
        price: Math.round(Number(item.total || item.amount || 0) * 100),
        unitQty: 1,
        note: item.description || ''
      }));

      // If no items, create a single item for the total
      if (items.length === 0) {
        items.push({
          name: `Invoice ${invoice.invoice_number}`,
          price: Math.round(Number(invoice.balance_due || invoice.total_amount || 0) * 100),
          unitQty: 1
        });
      }

      const nameParts = (invoice.customer_name || '').split(' ');

      const res = await axios.post(`${BACKEND_URL}/api/clover/hosted-checkout`, {
        customer_email: invoice.customer_email || '',
        customer_first_name: nameParts[0] || '',
        customer_last_name: nameParts.slice(1).join(' ') || '',
        items,
        invoice_id: invoice.id,
        return_path: `/pay/invoice/success?invoice=${invoice.id}`
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout session. Please try again.');
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-500">{error}</p>
        </Card>
      </div>
    );
  }

  if (invoice?.status === 'paid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Already Paid</h2>
          <p className="text-gray-500 mb-2">Invoice #{invoice.invoice_number} has been paid.</p>
          <p className="text-lg font-semibold text-green-600">${Number(invoice.total_amount).toFixed(2)}</p>
        </Card>
      </div>
    );
  }

  const balanceDue = invoice.balance_due || invoice.total_amount || 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Redefyne</h1>
          <p className="text-sm text-gray-500">Interior & Exterior</p>
        </div>

        {/* Invoice Summary */}
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-teal-600" />
                <span className="font-bold text-lg">Invoice #{invoice.invoice_number}</span>
              </div>
              <Badge className="bg-amber-100 text-amber-800">{invoice.status?.toUpperCase()}</Badge>
            </div>

            <div className="border-t pt-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Billed To</span>
                <span className="font-medium">{invoice.customer_name}</span>
              </div>
              {invoice.due_date && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Due Date</span>
                  <span className="font-medium">{new Date(invoice.due_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Line Items */}
            <div className="border-t pt-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs">
                    <th className="text-left pb-2">Item</th>
                    <th className="text-right pb-2">Qty</th>
                    <th className="text-right pb-2">Price</th>
                    <th className="text-right pb-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(invoice.items || []).map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="py-2">{item.name || item.service}</td>
                      <td className="py-2 text-right">{item.quantity || item.hours}</td>
                      <td className="py-2 text-right">${Number(item.unit_price || item.rate || 0).toFixed(2)}</td>
                      <td className="py-2 text-right font-medium">${Number(item.total || item.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="border-t pt-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span>Invoice Total</span>
                <span className="font-medium">${Number(invoice.total_amount).toFixed(2)}</span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Already Paid</span>
                  <span>-${Number(invoice.amount_paid).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                <span>Amount Due</span>
                <span className="text-teal-700">${Number(balanceDue).toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pay Now Button */}
        <Card>
          <CardContent className="p-6">
            <Button
              onClick={handlePayNow}
              disabled={redirecting}
              className="w-full h-12 text-lg bg-teal-600 hover:bg-teal-700"
              data-testid="pay-now-btn"
            >
              {redirecting ? (
                <><Loader2 size={20} className="mr-2 animate-spin" /> Redirecting to payment...</>
              ) : (
                <><CreditCard size={20} className="mr-2" /> Pay ${Number(balanceDue).toFixed(2)} Now <ExternalLink size={16} className="ml-2" /></>
              )}
            </Button>
            <p className="text-center text-xs text-gray-400 mt-3">
              You'll be redirected to Clover's secure payment page
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-400">
          Redefyne Interior and Exterior | 2301 S Stearman Dr, Chandler, AZ 85286
        </p>
      </div>
    </div>
  );
}
